import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Fix default marker icon issue with Leaflet
delete L?.Icon?.Default?.prototype?._getIconUrl;
L?.Icon?.Default?.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to update map view
const MapUpdater = ({ center, polyline }) => {
  const map = useMap();
  
  useEffect(() => {
    if (polyline && polyline?.length > 0) {
      // Fit bounds to show entire route
      const bounds = L?.latLngBounds(polyline?.map(coord => [coord?.[1], coord?.[0]]));
      map?.fitBounds(bounds, { padding: [50, 50] });
    } else if (center) {
      map?.setView(center, 13);
    }
  }, [center, polyline, map]);
  
  return null;
};

const RouteMap = ({ 
  routeName = '',
  routePolyline = [],
  startPoint = null,
  endPoint = null,
  stops = [],
  userLocation = null,
  showPolyline = true,
  onTogglePolyline
}) => {
  const defaultCenter = [37.8746, 32.4932]; // Konya coordinates as fallback
  const center = userLocation 
    ? [userLocation?.latitude, userLocation?.longitude] 
    : startPoint
    ? [startPoint?.latitude, startPoint?.longitude]
    : defaultCenter;

  // Convert polyline coordinates from [lng, lat] to [lat, lng] for Leaflet
  const polylineCoordinates = routePolyline?.map(coord => [coord?.[1], coord?.[0]]) || [];

  return (
    <div className="relative w-full h-64 md:h-80 lg:h-96 bg-muted rounded-xl overflow-hidden">
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapUpdater center={center} polyline={routePolyline} />
        
        {/* User location marker */}
        {userLocation && (
          <Marker position={[userLocation?.latitude, userLocation?.longitude]}>
            <Popup>
              <div className="text-sm">
                <strong>Konumunuz</strong>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Route polyline */}
        {showPolyline && polylineCoordinates?.length > 0 && (
          <Polyline
            positions={polylineCoordinates}
            pathOptions={{
              color: '#3b82f6',
              weight: 4,
              opacity: 0.7
            }}
          >
            <Popup>
              <div className="text-sm">
                <strong>{routeName}</strong>
                <br />
                {startPoint?.name && endPoint?.name && (
                  <span>{startPoint?.name} → {endPoint?.name}</span>
                )}
              </div>
            </Popup>
          </Polyline>
        )}
        
        {/* Start point marker */}
        {startPoint && (
          <Marker 
            position={[startPoint?.latitude, startPoint?.longitude]}
            icon={L?.divIcon({
              className: 'custom-marker',
              html: `<div style="background-color: #10b981; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12]
            })}
          >
            <Popup>
              <div className="text-sm">
                <strong>Başlangıç</strong>
                <br />
                {startPoint?.name || 'Başlangıç Noktası'}
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* End point marker */}
        {endPoint && (
          <Marker 
            position={[endPoint?.latitude, endPoint?.longitude]}
            icon={L?.divIcon({
              className: 'custom-marker',
              html: `<div style="background-color: #ef4444; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12]
            })}
          >
            <Popup>
              <div className="text-sm">
                <strong>Bitiş</strong>
                <br />
                {endPoint?.name || 'Bitiş Noktası'}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
      
      {/* Toggle polyline button */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-card/95 backdrop-blur-sm rounded-lg p-3 shadow-lg z-[1000]">
        <div className="flex items-center gap-2">
          <Icon name="Route" size={20} color="var(--color-primary)" />
          <span className="text-sm font-medium text-foreground">{routeName}</span>
        </div>
        {polylineCoordinates?.length > 0 && (
          <Button
            variant={showPolyline ? "default" : "outline"}
            size="sm"
            onClick={onTogglePolyline}
            iconName={showPolyline ? "Eye" : "EyeOff"}
            iconPosition="left"
          >
            Rota
          </Button>
        )}
      </div>
    </div>
  );
};

export default RouteMap;